import React from 'react';
import { X, CreditCard, User, AlertTriangle, CheckCircle, XCircle, Clock, Calendar, DollarSign, FileText, Shield } from 'lucide-react';

const PaymentLogDetailModal = ({ log, onClose }) => {
  if (!log) return null;

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTransactionTypeDisplay = (type) => {
    if (type === "New - Renew") return "SUBSCRIBE/RENEW";
    if (type === "renewal") return "RECURRING PAYMENT";
    return type;
  };

  const getStatusIcon = () => {
    switch(log.decision) {
      case 'ACCEPT':
        return <CheckCircle size={32} className="text-green-600" />;
      case 'DECLINE':
      case 'ERROR':
        return <XCircle size={32} className="text-red-600" />;
      case 'REVIEW':
        return <Clock size={32} className="text-yellow-600" />;
      default:
        return <AlertTriangle size={32} className="text-gray-600" />;
    }
  };

  const getStatusColor = () => {
    switch(log.decision) {
      case 'ACCEPT':
        return 'from-green-600 to-green-700';
      case 'DECLINE':
      case 'ERROR':
        return 'from-red-600 to-red-700';
      case 'REVIEW':
        return 'from-yellow-600 to-yellow-700';
      default:
        return 'from-gray-600 to-gray-700';
    }
  };

  const isFailed = log.decision === 'DECLINE' || log.decision === 'ERROR';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full mx-4 mb-8">
        {/* Header */}
        <div className={`sticky top-0 z-10 p-6 border-b bg-gradient-to-r ${getStatusColor()} text-white rounded-t-lg`}>
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-3 mb-2">
                {getStatusIcon()}
                <div>
                  <h2 className="text-2xl font-bold">Payment Transaction Details</h2>
                  <p className="text-sm opacity-90">Complete transaction information and status</p>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Transaction Status Banner */}
          <div className={`p-6 rounded-xl border-2 ${
            log.decision === 'ACCEPT' 
              ? 'bg-green-50 border-green-300' 
              : log.decision === 'REVIEW'
              ? 'bg-yellow-50 border-yellow-300'
              : 'bg-red-50 border-red-300'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className={`text-2xl font-bold ${
                  log.decision === 'ACCEPT' 
                    ? 'text-green-700' 
                    : log.decision === 'REVIEW'
                    ? 'text-yellow-700'
                    : 'text-red-700'
                }`}>
                  {log.decision === 'ACCEPT' ? 'Transaction Successful' :
                   log.decision === 'REVIEW' ? 'Transaction Under Review' :
                   'Transaction Failed'}
                </h3>
                <p className="text-gray-700 mt-1">{log.message}</p>
                {log.status && (
                  <p className="text-sm text-gray-600 mt-1">Status: {log.status}</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">{log.amount} {log.currency}</p>
                <p className="text-sm text-gray-600">{formatDate(log.createdAt)}</p>
              </div>
            </div>
          </div>

          {/* Failure Reason (if failed) */}
          {isFailed && (
            <div className="bg-red-50 border-l-4 border-red-500 p-5 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-red-600 mt-1" size={24} />
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-red-800 mb-2">Failure Information</h4>
                  {log.reason_code && (
                    <div className="mb-2">
                      <span className="font-semibold text-red-700">Reason Code:</span>
                      <span className="ml-2 text-red-800 font-mono">{log.reason_code}</span>
                    </div>
                  )}
                  <div className="text-red-700">
                    <span className="font-semibold">Message:</span>
                    <p className="mt-1">{log.message || 'Transaction was declined'}</p>
                  </div>
                  <div className="mt-3 p-3 bg-red-100 rounded">
                    <p className="text-sm text-red-800">
                      <strong>Common Reasons:</strong>
                    </p>
                    <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                      <li>Insufficient funds</li>
                      <li>Card expired or invalid</li>
                      <li>Incorrect CVV or card details</li>
                      <li>Bank declined the transaction</li>
                      <li>Card not authorized for online payments</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Transaction Information */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <div className="bg-blue-600 p-2 rounded-lg">
                <FileText size={20} className="text-white" />
              </div>
              Transaction Information / معلومات المعاملة
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Transaction ID / رقم المعاملة</p>
                <p className="text-sm font-mono font-semibold text-gray-800 break-all">{log.transaction_id}</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Network Transaction ID</p>
                <p className="text-sm font-mono font-semibold text-gray-800 break-all">{log.network_transaction_id || 'N/A'}</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Transaction Type / نوع المعاملة</p>
                <p className="text-lg font-semibold text-gray-800">{getTransactionTypeDisplay(log.transaction_type)}</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Date & Time / التاريخ والوقت</p>
                <p className="text-sm font-semibold text-gray-800">{formatDate(log.createdAt)}</p>
              </div>
            </div>
          </div>

          {/* Client Information */}
          {log.client && (
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-2 border-purple-200">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <div className="bg-purple-600 p-2 rounded-lg">
                  <User size={20} className="text-white" />
                </div>
                Customer Information / معلومات العميل
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600">Name / الاسم</p>
                  <p className="text-lg font-semibold text-gray-800">{log.client.username}</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600">Mobile / الجوال</p>
                  <p className="text-lg font-semibold text-gray-800">{log.client.mobile}</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600">Email / البريد الإلكتروني</p>
                  <p className="text-sm font-semibold text-gray-800 break-all">{log.client.email}</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600">Client ID / رقم العميل</p>
                  <p className="text-lg font-semibold text-gray-800 font-mono">{log.client_id}</p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Information */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <div className="bg-green-600 p-2 rounded-lg">
                <CreditCard size={20} className="text-white" />
              </div>
              Payment Details / تفاصيل الدفع
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Amount / المبلغ</p>
                <p className="text-2xl font-bold text-green-600">{log.amount} {log.currency}</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Payment Method / طريقة الدفع</p>
                <p className="text-lg font-semibold text-gray-800">{log.payment_method || 'Card'}</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Card Type / نوع البطاقة</p>
                <p className="text-lg font-semibold text-gray-800">{log.card_type || 'N/A'}</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Masked Card Number / رقم البطاقة</p>
                <p className="text-lg font-mono font-semibold text-gray-800">{log.masked_card_number || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Subscription Information */}
          {log.clientSubscription && (
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border-2 border-orange-200">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <div className="bg-orange-600 p-2 rounded-lg">
                  <Calendar size={20} className="text-white" />
                </div>
                Subscription Details / تفاصيل الاشتراك
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600">Plan Name / اسم الخطة</p>
                  <p className="text-lg font-semibold text-gray-800">{log.clientSubscription.subscription_name}</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600">Plan Cost / تكلفة الخطة</p>
                  <p className="text-lg font-semibold text-gray-800">{log.clientSubscription.subscription_cost} {log.currency}</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600">Start Date / تاريخ البدء</p>
                  <p className="text-sm font-semibold text-gray-800">{formatDate(log.clientSubscription.start_date)}</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600">End Date / تاريخ الانتهاء</p>
                  <p className="text-sm font-semibold text-gray-800">{formatDate(log.clientSubscription.end_date)}</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600">Payment Status / حالة الدفع</p>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                    {log.clientSubscription.payment_status}
                  </span>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600">Subscription ID / رقم الاشتراك</p>
                  <p className="text-sm font-mono font-semibold text-gray-800">{log.clientSubscription.client_subscription_id}</p>
                </div>
              </div>
            </div>
          )}

          {/* Saved Payment Methods (if available) */}
          {log.client?.paymethods && log.client.paymethods.length > 0 && (
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6 border-2 border-indigo-200">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <div className="bg-indigo-600 p-2 rounded-lg">
                  <Shield size={20} className="text-white" />
                </div>
                Saved Payment Methods ({log.client.paymethods.length}) / طرق الدفع المحفوظة
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {log.client.paymethods.map((method) => (
                  <div key={method.id} className="bg-white rounded-lg p-4 border border-indigo-200">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-gray-800">{method.card_type_name}</p>
                        <p className="text-xs text-gray-600">{method.payment_method}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        method.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {method.status}
                      </span>
                    </div>
                    <p className="font-mono text-sm text-gray-700 mb-2">{method.card_number}</p>
                    <div className="text-xs text-gray-600 space-y-1">
                      <p>{method.firstname} {method.lastname}</p>
                      <p>{method.bill_to_city}, {method.bill_to_country}</p>
                      <p className="text-xs text-gray-500">Added: {new Date(method.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Technical Details */}
          <details className="bg-gray-50 rounded-lg border border-gray-200">
            <summary className="cursor-pointer p-4 font-semibold text-gray-700 hover:bg-gray-100 rounded-lg">
              Technical Details (Click to expand)
            </summary>
            <div className="p-4 border-t">
              <pre className="text-xs bg-gray-800 text-green-400 p-4 rounded overflow-auto max-h-96 font-mono">
{JSON.stringify(log, null, 2)}
              </pre>
            </div>
          </details>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50 rounded-b-lg flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <p>Transaction recorded at: {formatDate(log.createdAt)}</p>
            {log.updatedAt && log.updatedAt !== log.createdAt && (
              <p>Last updated: {formatDate(log.updatedAt)}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentLogDetailModal;